import { useCallback, useEffect, useState } from 'react';

/**
 * Homes a house hunter has saved, kept in THEIR browser.
 *
 * Not on the server, and that is the design rather than a shortcut: people
 * looking for a home have no account here — tenant logins are by invitation
 * from a landlord — and asking a stranger to sign up to remember three flats is
 * how they leave. The list lives on the device they browsed on, survives a
 * reload, and costs nobody an account.
 *
 * Storage can be missing or refuse (private windows, blocked site data), so
 * every read and write is guarded and the page works without it — saving just
 * stops persisting.
 */

const KEY = 'ns.savedHomes';
const EVENT = 'ns:saved-homes';
/** The server accepts at most sixty ids in one search. */
export const MAX_SAVED = 60;

function read(): string[] {
  try {
    const raw = window.localStorage.getItem(KEY);
    const ids = raw ? JSON.parse(raw) : [];
    return Array.isArray(ids) ? ids.filter((x) => typeof x === 'string').slice(0, MAX_SAVED) : [];
  } catch {
    return [];
  }
}

function write(ids: string[]) {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(ids.slice(0, MAX_SAVED)));
  } catch { /* storage refused: the in-memory list still works for this visit */ }
  // Every card and the header count update together, in this tab and others.
  window.dispatchEvent(new Event(EVENT));
}

export function useSavedHomes() {
  const [ids, setIds] = useState<string[]>(() => (typeof window === 'undefined' ? [] : read()));

  useEffect(() => {
    const sync = () => setIds(read());
    window.addEventListener(EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const toggle = useCallback((id: string) => {
    const current = read();
    const next = current.includes(id)
      ? current.filter((x) => x !== id)
      : [id, ...current].slice(0, MAX_SAVED);
    setIds(next);
    write(next);
  }, []);

  return { ids, isSaved: (id: string) => ids.includes(id), toggle };
}
